const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Design API functions
export const designApi = {
  // Upload a new design (supports multiple files)
  uploadDesign: async (files, metadata = {}) => {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];
    
    // Append all files with the field name 'design' expected by backend
    fileArray.forEach(file => {
      formData.append('design', file);
    });
    
    // Add metadata
    Object.keys(metadata).forEach(key => {
      formData.append(key, metadata[key]);
    });

    const token = localStorage.getItem('token');
    const isAuthenticated = !!token;

    // Choose endpoint based on authentication status
    const endpoint = isAuthenticated ? 
      `${API_BASE_URL}/designs/upload` : 
      `${API_BASE_URL}/designs/guest-upload`;

    const headers = {};
    if (isAuthenticated) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`🔄 Design upload: Using ${isAuthenticated ? 'authenticated' : 'guest'} endpoint (${fileArray.length} file(s))`);

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers
    });

    if (!response.ok) {
      // Try to get the actual error message from server
      let errorMessage = `Upload failed: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the status text
        console.log('Could not parse error response, using status text');
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(`✅ Design upload successful via ${isAuthenticated ? 'authenticated' : 'guest'} endpoint`);
    return result;
  },

  // Upload multiple designs
  uploadMultipleDesigns: async (files, metadata = {}) => {
    if (!files || files.length === 0) {
      throw new Error('Please select at least one file to upload');
    }

    const formData = new FormData();
    
    // Append all files
    Array.from(files).forEach(file => {
      formData.append('design', file);
    });
    
    // Add metadata
    Object.keys(metadata).forEach(key => {
      formData.append(key, metadata[key]);
    });

    const token = localStorage.getItem('token');
    const isAuthenticated = !!token;

    // Choose endpoint based on authentication status
    const endpoint = isAuthenticated ? 
      `${API_BASE_URL}/designs/upload-multiple` : 
      `${API_BASE_URL}/designs/guest-upload-multiple`;

    const headers = {};
    if (isAuthenticated) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`🔄 Multiple design upload: Using ${isAuthenticated ? 'authenticated' : 'guest'} endpoint for ${files.length} files`);

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers
    });

    if (!response.ok) {
      let errorMessage = `Upload failed: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        console.log('Could not parse error response, using status text');
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(`✅ Multiple design upload successful: ${result.results || result.data?.designs?.length || 0} files uploaded`);
    return result;
  },

  // Get user's designs
  getUserDesigns: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/designs?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch designs: ${response.statusText}`);
    }

    return response.json();
  },

  // Get a specific design
  getDesign: async (id) => {
    const response = await fetch(`${API_BASE_URL}/designs/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch design: ${response.statusText}`);
    }

    return response.json();
  },

  // Update design
  updateDesign: async (id, updates) => {
    const response = await fetch(`${API_BASE_URL}/designs/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Failed to update design: ${response.statusText}`);
    }

    return response.json();
  },

  // Delete design
  deleteDesign: async (id) => {
    const response = await fetch(`${API_BASE_URL}/designs/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete design: ${response.statusText}`);
    }

    return response.json();
  },

  // Validate design for product
  validateDesign: async (designId, productId) => {
    const response = await fetch(`${API_BASE_URL}/designs/${designId}/validate/${productId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to validate design: ${response.statusText}`);
    }

    return response.json();
  }
};

// Template API functions
export const templateApi = {
  // Get all templates
  getAllTemplates: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/templates?${queryParams}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch templates: ${response.statusText}`);
    }

    return response.json();
  },

  // Get template by ID
  getTemplate: async (id) => {
    const response = await fetch(`${API_BASE_URL}/templates/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.statusText}`);
    }

    return response.json();
  },

  // Get templates by category
  getTemplatesByCategory: async (category, params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/templates/category/${category}?${queryParams}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch templates: ${response.statusText}`);
    }

    return response.json();
  },

  // Get featured templates
  getFeaturedTemplates: async (limit = 8) => {
    const response = await fetch(`${API_BASE_URL}/templates/featured?limit=${limit}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch featured templates: ${response.statusText}`);
    }

    return response.json();
  },

  // Get free templates
  getFreeTemplates: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/templates/free?${queryParams}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch free templates: ${response.statusText}`);
    }

    return response.json();
  },

  // Get popular templates
  getPopularTemplates: async (limit = 8) => {
    const response = await fetch(`${API_BASE_URL}/templates/popular?limit=${limit}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch popular templates: ${response.statusText}`);
    }

    return response.json();
  },

  // Search templates
  searchTemplates: async (query, params = {}) => {
    const queryParams = new URLSearchParams({ q: query, ...params });
    const response = await fetch(`${API_BASE_URL}/templates/search?${queryParams}`);

    if (!response.ok) {
      throw new Error(`Failed to search templates: ${response.statusText}`);
    }

    return response.json();
  },

  // Customize template
  customizeTemplate: async (templateId, customizations) => {
    const response = await fetch(`${API_BASE_URL}/templates/${templateId}/customize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(customizations)
    });

    if (!response.ok) {
      throw new Error(`Failed to customize template: ${response.statusText}`);
    }

    return response.json();
  },

  // Download template
  downloadTemplate: async (templateId, format = 'pdf') => {
    const response = await fetch(`${API_BASE_URL}/templates/${templateId}/download?format=${format}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download template: ${response.statusText}`);
    }

    // Return blob for file download
    return response.blob();
  },

  // Track template usage
  trackTemplateUsage: async (templateId, action = 'view') => {
    try {
      await fetch(`${API_BASE_URL}/templates/${templateId}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });
    } catch (error) {
      // Silently fail for analytics
      console.warn('Failed to track template usage:', error);
    }
  }
};

// Paper type API functions
export const paperTypeApi = {
  // Get paper types for a product
  getPaperTypes: async (productId) => {
    const response = await fetch(`${API_BASE_URL}/products/${productId}/paper-types`);

    if (!response.ok) {
      throw new Error(`Failed to fetch paper types: ${response.statusText}`);
    }

    return response.json();
  },

  // Get all available paper types
  getAllPaperTypes: async () => {
    const response = await fetch(`${API_BASE_URL}/paper-types`);

    if (!response.ok) {
      throw new Error(`Failed to fetch paper types: ${response.statusText}`);
    }

    return response.json();
  }
};

// File validation utilities
export const fileValidation = {
  // Validate file type
  validateFileType: (file, allowedTypes = []) => {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return allowedTypes.includes(fileExtension);
  },

  // Validate file size
  validateFileSize: (file, maxSizeMB = 50) => {
    const fileSizeMB = file.size / (1024 * 1024);
    return fileSizeMB <= maxSizeMB;
  },

  // Get image dimensions
  getImageDimensions: (file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('File is not an image'));
        return;
      }

      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          aspectRatio: img.width / img.height
        });
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  },

  // Validate image specifications
  validateImageSpecs: async (file, specs) => {
    const errors = [];

    try {
      const dimensions = await fileValidation.getImageDimensions(file);

      if (specs.minWidth && dimensions.width < specs.minWidth) {
        errors.push(`Image width must be at least ${specs.minWidth}px. Current: ${dimensions.width}px`);
      }

      if (specs.minHeight && dimensions.height < specs.minHeight) {
        errors.push(`Image height must be at least ${specs.minHeight}px. Current: ${dimensions.height}px`);
      }

      if (specs.aspectRatio && Math.abs(dimensions.aspectRatio - specs.aspectRatio) > 0.1) {
        errors.push(`Image aspect ratio should be ${specs.aspectRatio}:1. Current: ${dimensions.aspectRatio.toFixed(2)}:1`);
      }

      if (specs.maxWidth && dimensions.width > specs.maxWidth) {
        errors.push(`Image width must be less than ${specs.maxWidth}px. Current: ${dimensions.width}px`);
      }

      if (specs.maxHeight && dimensions.height > specs.maxHeight) {
        errors.push(`Image height must be less than ${specs.maxHeight}px. Current: ${dimensions.height}px`);
      }

    } catch (error) {
      errors.push('Unable to validate image dimensions');
    }

    return errors;
  }
};

// Order API functions for design orders
export const orderApi = {
  // Create order with design
  createOrderWithDesign: async (orderData) => {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(orderData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create order: ${response.statusText}`);
    }

    return response.json();
  },

  // Get order with design details
  getOrderWithDesign: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch order: ${response.statusText}`);
    }

    return response.json();
  }
};

// Error handling utility
export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.message.includes('Failed to fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  
  if (error.message.includes('401')) {
    return 'Authentication failed. Please log in again.';
  }
  
  if (error.message.includes('403')) {
    return 'You do not have permission to perform this action.';
  }
  
  if (error.message.includes('404')) {
    return 'The requested resource was not found.';
  }
  
  if (error.message.includes('413')) {
    return 'File size too large. Please choose a smaller file.';
  }
  
  if (error.message.includes('415')) {
    return 'File type not supported. Please choose a different file format.';
  }
  
  return error.message || 'An unexpected error occurred. Please try again.';
};
